"use client";

import { useMemo } from "react";
import Cards from "@/components/content/cards";
import { useContentSuggestions } from "@/hooks/useContentSuggestions";
import {
  buildContentAnalyticsPayload,
  getAnalyticsToken,
  sendPlayIntent,
} from "@/services/mediaAnalyticsService";

export const suggestionTitleByContentKind = {
  movie: "فیلم های پیشنهادی",
  series: "سریال های پیشنهادی",
  program: "برنامه های پیشنهادی",
  animation: "انیمیشن های پیشنهادی",
  music: "موسیقی های پیشنهادی",
  sport: "محتوا های ورزشی پیشنهادی",
  sports: "محتوا های ورزشی پیشنهادی",
  live: "زنده ببینید",
};

export const getSuggestionTitle = (contentKind) =>
  suggestionTitleByContentKind[contentKind] || "محتوای پیشنهادی";

function SuggestionDetails({ item }) {
  const englishTitle =
    typeof item.content?.titleEN === "string"
      ? item.content.titleEN.trim().replace(/\s+/g, " ")
      : "";

  if (!englishTitle) return null;

  return (
    <span
      className="mt-0.5 block min-w-0 w-full truncate text-right font-sans text-xs md:text-sm text-[rgba(255,255,255,0.56)]"
      dir="ltr"
      lang="en"
      title={englishTitle}
    >
      {englishTitle}
    </span>
  );
}

export default function ContentSuggestionsRail({
  slug,
  type = "movie",
  contentKind = type,
  enabled = true,
  className = "mx-auto w-full my-10 relative z-10 md:container px-5 2xl:px-0 xlg:max-w-[1920px]",
}) {
  const { data, isLoading, isAuthenticated } = useContentSuggestions(slug, {
    enabled,
  });
  const suggestionTitle = getSuggestionTitle(contentKind);
  const analyticsContext = useMemo(
    () => ({
      surface: "content_detail_suggestions",
      source: data?.strategyVersion,
      containerKey: data?.sourceSlug,
      enabled: isAuthenticated,
      minimumVisibleMs: 1000,
      requireAuth: true,
    }),
    [data?.strategyVersion, data?.sourceSlug, isAuthenticated]
  );

  if (isLoading) {
    return (
      <section
        className={className}
        aria-label={`در حال بارگذاری ${suggestionTitle}`}
        aria-busy="true"
      >
        <div aria-hidden="true">
          <div className="h-8 w-44 rounded bg-zinc-800 mb-5" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 overflow-hidden">
            {Array.from({ length: 6 }, (_, index) => (
              <div
                key={index}
                className={
                  index > 3
                    ? "hidden lg:block"
                    : index > 1
                    ? "hidden md:block"
                    : ""
                }
              >
                <div
                  className={`bg-zinc-800 rounded ${
                    type === "music" ? "aspect-square" : "aspect-[9/13]"
                  }`}
                />
                <div className="h-5 w-3/4 bg-zinc-800 rounded mt-3" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!data?.results.length) return null;

  const handleSelect = (content, position) => {
    if (!isAuthenticated || !getAnalyticsToken()) return;
    const payload = buildContentAnalyticsPayload(content, {
      surface: analyticsContext.surface,
      source: analyticsContext.source,
      containerKey: analyticsContext.containerKey,
      position,
    });
    if (payload) sendPlayIntent(payload);
  };

  return (
    <section className={className} aria-label={suggestionTitle}>
      <Cards
        key={`${data.sourceSlug}:${data.strategyVersion}:${data.generatedAt}:${isAuthenticated}`}
        items={data.results}
        categoryName={suggestionTitle}
        showTitle
        hasExpand={false}
        isSwiper
        preserveOrder
        // The CMS has already applied profile eligibility; summary ageRange is optional.
        filterByAgeRange={false}
        noContainer
        type={type}
        analyticsContext={analyticsContext}
        onItemSelect={handleSelect}
        renderItemDetails={(item) => <SuggestionDetails item={item} />}
      />
    </section>
  );
}
